// Dvoufázové ověření: zapnout (QR + tajný klíč + první kód), záložní kódy (ukážou se jednou), vypnout.
import { registerComponent } from '@core/views/factory';
import { core, ApiError } from '@core/api';
import { h, clear } from '@core/dom';
import { t, tk, fmtDateTime } from '@core/i18n';
import { icon } from '@core/icons';
import { toast } from '@core/components/toast';
import { prompt, alert } from '@core/components/dialog';
import { qrSvg } from '@core/components/qr';

registerComponent('desktop-totp', ({ desc }) => {
  const p = desc.payload as { enabled: boolean; enabledAt: string | null; backupLeft: number; issuer: string; login: string };
  const body = h('div');
  let state = { enabled: p.enabled, enabledAt: p.enabledAt, backupLeft: p.backupLeft };

  const showBackup = (codes: string[]): HTMLElement => {
    const box = h('div', { class: 'totp-backup' }, h('p', null, t('desktop.totp.backup_intro')), h('div', { class: 'totp-codes' }, codes.map((c) => h('code', null, c))));
    const copy = h('button', { type: 'button', class: 'btn sm' }, icon('copy'), t('desktop.apppw.copy'));
    copy.addEventListener('click', () => void navigator.clipboard?.writeText(codes.join('\n')).then(() => toast(t('desktop.apppw.copied'), 'ok')));
    box.appendChild(copy);
    return box;
  };

  function render(extra?: HTMLElement): void {
    clear(body);
    if (!state.enabled) {
      const start = h('button', { type: 'button', class: 'btn primary' }, icon('shield'), t('desktop.totp.enable'));
      start.addEventListener('click', () => void begin());
      body.append(h('p', { class: 'muted small' }, t('desktop.totp.intro')), h('div', { class: 'row' }, start));
    } else {
      const disable = h('button', { type: 'button', class: 'btn danger' }, t('desktop.totp.disable'));
      disable.addEventListener('click', async () => {
        const code = await prompt(t('desktop.totp.enter_code'), '', { title: t('desktop.totp.disable') });
        if (code === null) return;
        try {
          await core('totp', 'disable', { code });
          state = { enabled: false, enabledAt: null, backupLeft: 0 };
          toast(t('desktop.totp.disabled'), 'ok');
          render();
        } catch (e) {
          toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
        }
      });
      const regen = h('button', { type: 'button', class: 'btn' }, t('desktop.totp.new_backup'));
      regen.addEventListener('click', async () => {
        const code = await prompt(t('desktop.totp.enter_code'), '', { title: t('desktop.totp.new_backup') });
        if (code === null) return;
        try {
          const r = await core<{ codes: string[] }>('totp', 'backup', { code });
          state.backupLeft = r.codes.length;
          render(showBackup(r.codes));
        } catch (e) {
          toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
        }
      });
      body.append(
        h('p', null, icon('check', 'icon cell-check'), ' ', t('desktop.totp.enabled_since', { when: state.enabledAt ? fmtDateTime(state.enabledAt) : '—' })),
        h('p', { class: 'muted small' }, t('desktop.totp.backup_left', { n: state.backupLeft })),
        h('div', { class: 'row' }, regen, disable),
      );
    }
    if (extra) body.appendChild(extra);
  }

  async function begin(): Promise<void> {
    try {
      const r = await core<{ secret: string; otpauth: string }>('totp', 'begin');
      const codeInput = h('input', { class: 'input', inputmode: 'numeric', autocomplete: 'one-time-code', placeholder: '123 456', maxlength: '7' });
      const confirmBtn = h('button', { type: 'button', class: 'btn primary' }, t('desktop.totp.confirm'));
      const qr = h('div', { class: 'totp-qr' });
      qr.innerHTML = qrSvg(r.otpauth, 200);
      const wrap = h(
        'div',
        { class: 'totp-setup' },
        h('h3', null, t('desktop.totp.step1')),
        h('p', { class: 'muted small' }, t('desktop.totp.step1_text')),
        qr,
        h('p', { class: 'small' }, t('desktop.totp.manual'), ' ', h('code', { class: 'totp-secret' }, r.secret.replace(/(.{4})/g, '$1 ').trim())),
        h('h3', null, t('desktop.totp.step2')),
        h('div', { class: 'row' }, codeInput, confirmBtn),
      );
      confirmBtn.addEventListener('click', async () => {
        try {
          const res = await core<{ codes: string[] }>('totp', 'enable', { secret: r.secret, code: codeInput.value });
          state = { enabled: true, enabledAt: new Date().toISOString(), backupLeft: res.codes.length };
          toast(t('desktop.totp.enabled'), 'ok');
          render(showBackup(res.codes));
        } catch (e) {
          toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
          codeInput.focus();
        }
      });
      codeInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') confirmBtn.click(); });
      clear(body);
      body.appendChild(wrap);
      codeInput.focus();
    } catch (e) {
      void alert(tk((e as ApiError).message, (e as ApiError).params ?? undefined));
    }
  }

  render();
  return { el: h('div', { class: 'view props-view totp-view' }, body) };
});
